import { defineStore } from 'pinia'
import { expectArray } from '../composables/expectShape'
import { useAsyncResource, UnauthorizedError } from '../composables/useAsyncResource'

// One day's point in the Activity tab's backlog burndown (#1738) — one row
// per repo per calendar day from the daily backlog snapshots. A day whose
// snapshot fetch failed has no entry at all (the chart draws a gap);
// `human_open_issue_count` is null on a day only its own fetch failed, and
// for every day before the column existed (2026-09-23).
export interface ProductOwnerBacklogDay {
  date: string
  open_issue_count: number
  human_open_issue_count: number | null
}

// Its own store rather than another fetcher on useProductOwnerRunsStore —
// that file sits at the 300-line limit, and the burndown's (repo, days)
// lifecycle is as independent of the run list as the hourly drill-down's.
export const useProductOwnerBacklogStore = defineStore('productOwnerBacklog', () => {
  let backlogRepo = ''
  let backlogDaysWindow = 0
  const {
    data: backlogDays,
    loaded: backlogLoaded,
    loading: backlogLoading,
    error: backlogLoadError,
    needsAuth: backlogNeedsAuth,
    refresh: refreshBacklog,
  } = useAsyncResource<ProductOwnerBacklogDay[]>(
    async () => {
      const url = `/api/v1/agent/product_owner_runs/backlog?repo=${encodeURIComponent(backlogRepo)}&days=${backlogDaysWindow}`
      const res = await fetch(url, { credentials: 'include' })
      if (res.status === 401) throw new UnauthorizedError()
      if (!res.ok) throw new Error(`Failed to load backlog history: ${res.status}`)
      const body = (await res.json()) as { repo: string; days: ProductOwnerBacklogDay[] }
      return expectArray<ProductOwnerBacklogDay>(body.days, 'product-owner backlog')
    },
    { initialValue: [] },
  )

  async function fetchBacklog(repo: string, days: number): Promise<void> {
    backlogRepo = repo
    backlogDaysWindow = days
    return refreshBacklog()
  }

  return { backlogDays, backlogLoaded, backlogLoading, backlogLoadError, backlogNeedsAuth, fetchBacklog }
})
